import { EventSchemas, EventType, type AGUIEvent } from '@ag-ui/core'
import type {
  CompleteAttachment,
  ThreadAssistantMessage,
  ThreadAssistantMessagePart,
  ThreadMessage,
} from '@assistant-ui/react'
import { getDmpChatAttachmentType } from '../dmpChatAttachmentType.ts'
import type { DmpChatToolDisplayMode } from '../dmpChatToolDisplay'

export const PROJECT_AGENT = {
  key: 'project',
  name: 'Project Agent',
} as const

export const DMP_CHAT_SUCCESS_SUMMARY = 'The Project Agent completed this request.'
export const DMP_CHAT_FAILURE_SUMMARY = 'The Project Agent could not complete this request.'
export const DMP_CHAT_CANCELLATION_SUMMARY = 'The request was cancelled.'
export const DMP_CHAT_CONNECTION_FAILURE_SUMMARY = 'The Project Agent connection failed. Try again.'
export const DMP_CHAT_TASK_FAILURE_SUMMARY = 'The Project Agent request could not be started. Try again.'
export const DMP_CHAT_TIMEOUT_SUMMARY = 'The Project Agent request timed out. Try again.'
export const DMP_CHAT_MAX_RESPONSE_CHARS = 250_000
export const DMP_CHAT_MAX_ACTIVITY_CHARS = 250_000
export const DMP_CHAT_USAGE_EVENT_NAME = 'dmp-chat-usage'
const DMP_CHAT_RESPONSE_TRUNCATED_NOTICE = '\n\n[Response truncated for performance.]'

export type DmpChatFixtureMode = 'success' | 'failure'

export type DmpChatTurnStatus = 'starting' | 'running' | 'cancellation-requested' | 'completed' | 'failed' | 'cancelled'

export type DmpChatTaskStatus = 'creating' | 'accepted' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timed-out'

export type DmpChatMessageStatus = 'complete' | 'pending' | 'streaming' | 'error' | 'cancelled'

export type DmpChatActivityPartStatus = 'streaming' | 'complete' | 'incomplete'

export interface DmpChatAttachment {
  inputRef: string
  filename: string
  contentType?: string
  kind?: string
  sizeBytes?: number
  file?: File
}

export interface DmpChatTextPart {
  type: 'text'
  id: string
  messageId: string
  text: string
  status: DmpChatActivityPartStatus
}

export interface DmpChatReasoningPart {
  type: 'reasoning'
  id: string
  messageId: string
  text: string
  status: DmpChatActivityPartStatus
}

export interface DmpChatToolCallPart {
  type: 'tool-call'
  id: string
  toolCallId: string
  toolName: string
  argsText: string
  result: string | null
  status: DmpChatActivityPartStatus
}

export type DmpChatActivityPart = DmpChatTextPart | DmpChatReasoningPart | DmpChatToolCallPart

export interface DmpChatUsage {
  input: number
  output: number
  reasoning?: number
  total: number
  contextWindow?: number
}

export type DmpChatTerminalOutcome = {
  type: 'success' | 'failure' | 'cancelled'
  summary: string
}

const TERMINAL_STATUS_BY_OUTCOME = {
  success: {
    turnStatus: 'completed',
    taskStatus: 'completed',
    messageStatus: 'complete',
  },
  failure: {
    turnStatus: 'failed',
    taskStatus: 'failed',
    messageStatus: 'error',
  },
  cancelled: {
    turnStatus: 'cancelled',
    taskStatus: 'cancelled',
    messageStatus: 'cancelled',
  },
} as const

export type DmpChatIdPrefix =
  | 'thread'
  | 'turn'
  | 'chat'
  | 'task'
  | 'request'
  | 'run'
  | 'user-message'
  | 'assistant-message'

export type DmpChatIdFactory = (prefix: DmpChatIdPrefix) => string

export interface DmpChatMessage {
  id: string
  turnId: string
  role: 'user' | 'assistant'
  text: string
  parts: readonly DmpChatActivityPart[]
  attachments: readonly DmpChatAttachment[]
  status: DmpChatMessageStatus
  isOptimistic: boolean
  createdAt: Date
}

/** A backend execution identity, intentionally distinct from a DmpChatTurn. */
export interface DmpChatTask {
  id: string
  status: DmpChatTaskStatus
  statusUrl: string | null
  resultUrl: string | null
}

export interface DmpChatTurn {
  id: string
  threadId: string
  /** Stable CollaborationHub channel identity for this page-session thread. */
  chatId: string
  /** Blob session key sent on every task so the worker can persist and replay this thread. */
  chatHistoryId: string
  task: DmpChatTask
  requestId: string
  runId: string
  userMessageId: string
  assistantMessageId: string
  /** Latest worker-side AG-UI text-message identity, which is not known when the turn starts. */
  streamMessageId: string | null
  prompt: string
  attachments: readonly DmpChatAttachment[]
  status: DmpChatTurnStatus
  createdAt: Date
  outcome: DmpChatTerminalOutcome | null
}

export interface DmpChatThread {
  id: string
  /** Stable persistence id for this page-session thread. Distinct from `id` / SignalR `chatId`. */
  chatHistoryId: string
  projectId: number
  agent: typeof PROJECT_AGENT
  messages: readonly DmpChatMessage[]
  turns: readonly DmpChatTurn[]
  activeTurnId: string | null
  lastOutcome: DmpChatTerminalOutcome | null
  lastUsage: DmpChatUsage | null
}

export interface DmpChatState {
  thread: DmpChatThread
}

export interface DmpChatHistoryMessage {
  id?: string | null
  role?: string | null
  content?: string | null
  attachments?:
    | readonly {
        inputRef?: string | null
        filename?: string | null
      }[]
    | null
  toolCalls?: readonly Record<string, unknown>[] | null
  tool_calls?: readonly Record<string, unknown>[] | null
  toolCallId?: string | null
  tool_call_id?: string | null
  cancelled?: boolean
  timestamp?: number
}

export interface DmpChatHistorySession {
  chatHistoryId?: string | null
  agentKey?: string | null
  messages?: readonly DmpChatHistoryMessage[] | null
  deleted?: boolean
}

export type DmpChatAction =
  | { type: 'submit-turn'; turn: DmpChatTurn }
  | { type: 'hydrate-history'; session: DmpChatHistorySession }
  | { type: 'new-chat' }
  | {
      type: 'task-accepted'
      turnId: string
      task: {
        id: string
        statusUrl?: string | null
        resultUrl?: string | null
      }
    }
  | { type: 'ingest-event'; event: AGUIEvent }
  | { type: 'ingest-raw-event'; rawEvent: string }
  | { type: 'request-cancellation'; turnId: string }
  | {
      type: 'reconcile-task'
      turnId: string
      taskId: string
      status: DmpChatTaskStatus
      message?: string | null
      usage?: unknown
    }
  | { type: 'finish-turn'; turnId: string; outcome: DmpChatTerminalOutcome }

let fallbackIdSequence = 0

export function createDmpChatId(prefix: DmpChatIdPrefix): string {
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${(fallbackIdSequence += 1).toString(36)}`

  return `${prefix}-${randomId}`
}

/** Unprefixed UUID used as the worker `chatHistoryId`. Empty / `dmpchat-dev` stay stateless. */
export function createDmpChatHistoryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  fallbackIdSequence += 1
  return `00000000-0000-4000-8000-${fallbackIdSequence.toString(16).padStart(12, '0')}`
}

export function createDmpChatState(projectId: number, idFactory: DmpChatIdFactory = createDmpChatId): DmpChatState {
  return {
    thread: {
      id: idFactory('thread'),
      chatHistoryId: createDmpChatHistoryId(),
      projectId,
      agent: PROJECT_AGENT,
      messages: [],
      turns: [],
      activeTurnId: null,
      lastOutcome: null,
      lastUsage: null,
    },
  }
}

export function createDmpChatTurn(
  thread: Pick<DmpChatThread, 'id' | 'chatHistoryId'>,
  prompt: string,
  idFactory: DmpChatIdFactory = createDmpChatId,
  attachments: readonly DmpChatAttachment[] = []
): DmpChatTurn {
  return {
    id: idFactory('turn'),
    threadId: thread.id,
    chatId: thread.id,
    chatHistoryId: thread.chatHistoryId,
    task: {
      id: idFactory('task'),
      status: 'creating',
      statusUrl: null,
      resultUrl: null,
    },
    requestId: idFactory('request'),
    runId: idFactory('run'),
    userMessageId: idFactory('user-message'),
    assistantMessageId: idFactory('assistant-message'),
    streamMessageId: null,
    prompt,
    attachments: attachments.map((attachment) => ({ ...attachment })),
    status: 'starting',
    createdAt: new Date(),
    outcome: null,
  }
}

export function isDmpChatTurnActive(turn: DmpChatTurn | null): boolean {
  return turn?.status === 'starting' || turn?.status === 'running' || turn?.status === 'cancellation-requested'
}

export function getActiveDmpChatTurn(state: DmpChatState): DmpChatTurn | null {
  if (!state.thread.activeTurnId) {
    return null
  }

  return state.thread.turns.find((turn) => turn.id === state.thread.activeTurnId) ?? null
}

export function getLatestDmpChatTurn(state: DmpChatState): DmpChatTurn | null {
  return state.thread.turns[state.thread.turns.length - 1] ?? null
}

export function normalizeDmpChatUsage(value: unknown): DmpChatUsage | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const valueRecord = value as Record<string, unknown>
  const nestedUsage = valueRecord.usage ?? valueRecord.Usage
  const usageRecord =
    nestedUsage && typeof nestedUsage === 'object' && !Array.isArray(nestedUsage)
      ? (nestedUsage as Record<string, unknown>)
      : valueRecord
  const input = readUsageCount(usageRecord, ['input', 'inputTokens', 'input_tokens', 'prompt_tokens'])
  const output = readUsageCount(usageRecord, ['output', 'outputTokens', 'output_tokens', 'completion_tokens'])
  const reasoning = readOptionalUsageCount(usageRecord, ['reasoning', 'reasoningTokens', 'reasoning_tokens'])
  const total = readUsageCount(usageRecord, ['total', 'totalTokens', 'total_tokens'])
  const contextWindow = readOptionalUsageCount(
    usageRecord,
    ['contextWindow', 'context_window', 'contextWindowLimit', 'context_window_limit'],
    1
  )

  if (input === null || output === null || total === null || reasoning === false || contextWindow === false) {
    return null
  }

  return {
    input,
    output,
    ...(reasoning !== null ? { reasoning } : {}),
    total,
    ...(contextWindow !== null ? { contextWindow } : {}),
  }
}

export function getDmpChatUsage(value: unknown): DmpChatUsage | null {
  if (typeof value === 'string') {
    try {
      return getDmpChatUsage(JSON.parse(value))
    } catch {
      return null
    }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const valueRecord = value as Record<string, unknown>
  const candidates = [
    valueRecord.usage,
    valueRecord.Usage,
    valueRecord.result,
    valueRecord.Result,
    valueRecord.metadata,
    valueRecord.Metadata,
    value,
  ]

  for (const candidate of candidates) {
    const usage = normalizeDmpChatUsage(candidate)
    if (usage) {
      return usage
    }
  }

  return null
}

function readUsageCount(record: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = record[key]
    if (value !== undefined) {
      return isUsageCount(value) ? value : null
    }
  }

  return null
}

function readOptionalUsageCount(
  record: Record<string, unknown>,
  keys: readonly string[],
  minimum: number = 0
): number | null | false {
  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null) {
      return isUsageCount(value) && value >= minimum ? value : false
    }
  }

  return null
}

function isUsageCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
}

export function normalizeDmpChatTaskStatus(value: unknown): DmpChatTaskStatus | null {
  if (typeof value !== 'string') {
    return null
  }

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
  switch (normalizedValue) {
    case 'creating':
      return 'creating'
    case 'accepted':
    case 'pending':
    case 'queued':
    case 'submitted':
    case 'scheduled':
      return 'accepted'
    case 'running':
    case 'started':
    case 'processing':
    case 'in-progress':
    case 'inprogress':
      return 'running'
    case 'completed':
    case 'complete':
    case 'success':
    case 'succeeded':
      return 'completed'
    case 'failed':
    case 'failure':
    case 'error':
      return 'failed'
    case 'cancelled':
    case 'canceled':
    case 'cancel':
      return 'cancelled'
    case 'timed-out':
    case 'timeout':
    case 'timedout':
      return 'timed-out'
    default:
      return null
  }
}

export function getDmpChatTaskResultStatus(result: unknown): DmpChatTaskStatus | null {
  if (typeof result === 'string') {
    try {
      return getDmpChatTaskResultStatus(JSON.parse(result))
    } catch {
      return normalizeDmpChatTaskStatus(result)
    }
  }

  if (!result || typeof result !== 'object') {
    return null
  }

  const resultRecord = result as Record<string, unknown>
  const directStatus = normalizeDmpChatTaskStatus(resultRecord.status ?? resultRecord.Status)
  if (directStatus) {
    return directStatus
  }

  const nestedResult = resultRecord.result ?? resultRecord.Result
  if (nestedResult) {
    const nestedResultStatus = getDmpChatTaskResultStatus(nestedResult)
    if (nestedResultStatus) {
      return nestedResultStatus
    }
  }

  const outcome = resultRecord.outcome ?? resultRecord.Outcome
  if (outcome && typeof outcome === 'object') {
    const outcomeRecord = outcome as Record<string, unknown>
    return normalizeDmpChatTaskStatus(outcomeRecord.status ?? outcomeRecord.Status ?? outcomeRecord.type)
  }

  return null
}

export function parseDmpChatEvent(rawEvent: unknown): AGUIEvent | null {
  if (typeof rawEvent !== 'string') {
    return null
  }

  try {
    const parsedJson: unknown = JSON.parse(rawEvent)
    const parsedEvent = EventSchemas.safeParse(parsedJson)

    return parsedEvent.success ? sanitizeDmpChatEvent(parsedEvent.data) : null
  } catch {
    return null
  }
}

export function reduceDmpChatState(state: DmpChatState, action: DmpChatAction): DmpChatState {
  switch (action.type) {
    case 'submit-turn':
      return submitTurn(state, action.turn)
    case 'hydrate-history':
      return hydrateHistory(state, action.session)
    case 'new-chat':
      return startNewChat(state)
    case 'task-accepted':
      return acceptTask(state, action.turnId, action.task)
    case 'ingest-event':
      return applyAgUiEvent(state, action.event)
    case 'ingest-raw-event': {
      const event = parseDmpChatEvent(action.rawEvent)
      return event ? applyAgUiEvent(state, event) : state
    }
    case 'request-cancellation':
      return requestCancellation(state, action.turnId)
    case 'reconcile-task':
      return reconcileTask(state, action.turnId, action.taskId, action.status, action.message, action.usage)
    case 'finish-turn': {
      const turn = getActiveDmpChatTurn(state)
      if (!turn || turn.id !== action.turnId) {
        return state
      }

      return finishTurn(state, turn, action.outcome)
    }
  }
}

function hydrateHistory(state: DmpChatState, session: DmpChatHistorySession): DmpChatState {
  if (getActiveDmpChatTurn(state) || session.deleted || !isNonEmptyHistoryId(session.chatHistoryId)) {
    return state
  }

  const nextState = createDmpChatState(state.thread.projectId)
  nextState.thread.chatHistoryId = session.chatHistoryId
  const hydrated = createHydratedHistory(nextState.thread, session.messages ?? [])

  return {
    thread: {
      ...nextState.thread,
      messages: hydrated.messages,
      turns: hydrated.turns,
    },
  }
}

function startNewChat(state: DmpChatState): DmpChatState {
  return getActiveDmpChatTurn(state) ? state : createDmpChatState(state.thread.projectId)
}

function isNonEmptyHistoryId(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function createHydratedHistory(
  thread: DmpChatThread,
  historyMessages: readonly DmpChatHistoryMessage[]
): { messages: DmpChatMessage[]; turns: DmpChatTurn[] } {
  const toolResults = collectHistoryToolResults(historyMessages)
  const messages: DmpChatMessage[] = []
  const turns: DmpChatTurn[] = []

  let index = 0
  while (index < historyMessages.length) {
    const userMessage = historyMessages[index]
    if (getHistoryRole(userMessage) !== 'user') {
      index += 1
      continue
    }

    const segmentEnd = findNextHistoryUserMessage(historyMessages, index + 1)
    const assistantMessages = historyMessages
      .slice(index + 1, segmentEnd)
      .filter((message) => getHistoryRole(message) === 'assistant')
    const assistantMessage = assistantMessages.at(-1)
    if (!assistantMessage) {
      index = segmentEnd
      continue
    }

    const toolCalls = assistantMessages.flatMap((message) => getHistoryToolCalls(message))
    const assistantText = getHistoryContent(assistantMessage)
    if (!assistantMessage.cancelled && !assistantText && toolCalls.length === 0) {
      index = segmentEnd
      continue
    }

    const attachments = getHistoryAttachments(userMessage)
    const turn = createHydratedTurn(
      thread,
      getHistoryContent(userMessage),
      assistantMessage.cancelled === true,
      attachments
    )
    turns.push(turn)
    messages.push(createHydratedUserMessage(userMessage, turn))
    messages.push(createHydratedAssistantMessage(assistantMessage, turn, toolCalls, toolResults))
    index = segmentEnd
  }

  return { messages, turns }
}

function collectHistoryToolResults(historyMessages: readonly DmpChatHistoryMessage[]): ReadonlyMap<string, string> {
  const results = new Map<string, string>()
  for (const message of historyMessages) {
    if (getHistoryRole(message) !== 'tool') {
      continue
    }

    const toolCallId = getHistoryString(message, ['toolCallId', 'tool_call_id'])
    if (toolCallId) {
      results.set(toolCallId, getHistoryContent(message))
    }
  }
  return results
}

function findNextHistoryUserMessage(messages: readonly DmpChatHistoryMessage[], startIndex: number): number {
  for (let index = startIndex; index < messages.length; index += 1) {
    if (getHistoryRole(messages[index]) === 'user') {
      return index
    }
  }
  return messages.length
}

function getHistoryRole(message: DmpChatHistoryMessage | undefined): string {
  return typeof message?.role === 'string' ? message.role.trim().toLowerCase() : ''
}

function getHistoryContent(message: DmpChatHistoryMessage | undefined): string {
  return typeof message?.content === 'string' ? message.content : ''
}

function getHistoryAttachments(message: DmpChatHistoryMessage | undefined): DmpChatAttachment[] {
  return (message?.attachments ?? []).flatMap((attachment) => {
    const inputRef = typeof attachment.inputRef === 'string' ? attachment.inputRef.trim() : ''
    const filename = typeof attachment.filename === 'string' ? attachment.filename.trim() : ''
    if (!inputRef || !filename) {
      return []
    }

    return [{ inputRef, filename }]
  })
}

function getHistoryString(
  message: DmpChatHistoryMessage | Record<string, unknown>,
  keys: readonly string[]
): string | undefined {
  const record = message as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return undefined
}

function getHistoryToolCalls(message: DmpChatHistoryMessage): Record<string, unknown>[] {
  const toolCalls = message.toolCalls ?? message.tool_calls
  return Array.isArray(toolCalls)
    ? toolCalls.filter((toolCall): toolCall is Record<string, unknown> => isHistoryRecord(toolCall))
    : []
}

function isHistoryRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function createHydratedTurn(
  thread: DmpChatThread,
  prompt: string,
  cancelled: boolean,
  attachments: readonly DmpChatAttachment[]
): DmpChatTurn {
  const turn = createDmpChatTurn(thread, prompt, createDmpChatId, attachments)
  const terminalStatus = cancelled ? 'cancelled' : 'completed'
  return {
    ...turn,
    task: {
      ...turn.task,
      status: terminalStatus,
    },
    status: terminalStatus === 'cancelled' ? 'cancelled' : 'completed',
    outcome: {
      type: cancelled ? 'cancelled' : 'success',
      summary: cancelled ? DMP_CHAT_CANCELLATION_SUMMARY : DMP_CHAT_SUCCESS_SUMMARY,
    },
  }
}

function createHydratedUserMessage(message: DmpChatHistoryMessage, turn: DmpChatTurn): DmpChatMessage {
  const text = getHistoryContent(message)
  return {
    id: getHistoryMessageId(message, turn.userMessageId),
    turnId: turn.id,
    role: 'user',
    text,
    parts: [
      {
        type: 'text',
        id: getHistoryMessageId(message, turn.userMessageId),
        messageId: getHistoryMessageId(message, turn.userMessageId),
        text,
        status: 'complete',
      },
    ],
    attachments: turn.attachments,
    status: 'complete',
    isOptimistic: false,
    createdAt: getHistoryMessageDate(message, turn.createdAt),
  }
}

function createHydratedAssistantMessage(
  message: DmpChatHistoryMessage,
  turn: DmpChatTurn,
  toolCalls: readonly Record<string, unknown>[],
  toolResults: ReadonlyMap<string, string>
): DmpChatMessage {
  const text = getHistoryContent(message)
  const parts: DmpChatActivityPart[] = toolCalls
    .map((toolCall) => createHydratedToolCallPart(toolCall, toolResults))
    .filter((part): part is DmpChatToolCallPart => part !== null)
  if (text) {
    parts.push({
      type: 'text',
      id: `${turn.assistantMessageId}-text`,
      messageId: turn.assistantMessageId,
      text,
      status: 'complete',
    })
  }

  const id = getHistoryMessageId(message, turn.assistantMessageId)
  return {
    id,
    turnId: turn.id,
    role: 'assistant',
    text,
    parts,
    attachments: [],
    status: message.cancelled ? 'cancelled' : 'complete',
    isOptimistic: false,
    createdAt: getHistoryMessageDate(message, turn.createdAt),
  }
}

function createHydratedToolCallPart(
  toolCall: Record<string, unknown>,
  toolResults: ReadonlyMap<string, string>
): DmpChatToolCallPart | null {
  const nestedFunction = isHistoryRecord(toolCall.function) ? toolCall.function : undefined
  const toolCallId =
    getHistoryString(toolCall, ['toolCallId', 'tool_call_id', 'id']) ??
    (nestedFunction ? getHistoryString(nestedFunction, ['toolCallId', 'tool_call_id', 'id']) : undefined)
  const toolName =
    getHistoryString(toolCall, ['toolName', 'tool_name', 'name']) ??
    (nestedFunction ? getHistoryString(nestedFunction, ['name']) : undefined)
  if (!toolCallId || !toolName) {
    return null
  }

  const result = toolResults.get(toolCallId) ?? null
  return {
    type: 'tool-call',
    id: `history-tool-${toolCallId}`,
    toolCallId,
    toolName,
    argsText: getHistoryToolArguments(toolCall, nestedFunction),
    result,
    status: result === null ? 'incomplete' : 'complete',
  }
}

function getHistoryToolArguments(
  toolCall: Record<string, unknown>,
  nestedFunction: Record<string, unknown> | undefined
): string {
  const argumentsValue =
    toolCall.arguments ??
    toolCall.args ??
    toolCall.input ??
    toolCall.toolArgs ??
    toolCall.tool_args ??
    nestedFunction?.arguments
  if (typeof argumentsValue === 'string') {
    return argumentsValue
  }
  if (argumentsValue === undefined) {
    return ''
  }
  return JSON.stringify(argumentsValue) ?? ''
}

function getHistoryMessageId(message: DmpChatHistoryMessage, fallback: string): string {
  return isNonEmptyHistoryId(message.id) ? message.id : fallback
}

function getHistoryMessageDate(message: DmpChatHistoryMessage, fallback: Date): Date {
  return typeof message.timestamp === 'number' ? new Date(message.timestamp) : fallback
}

function acceptTask(
  state: DmpChatState,
  turnId: string,
  task: {
    id: string
    statusUrl?: string | null
    resultUrl?: string | null
  }
): DmpChatState {
  const turn = getActiveDmpChatTurn(state)
  if (!turn || turn.id !== turnId) {
    return state
  }

  return replaceTurnAndMessages(
    state,
    turnId,
    (currentTurn) => ({
      ...currentTurn,
      task: {
        id: task.id,
        status: 'accepted',
        statusUrl: task.statusUrl ?? null,
        resultUrl: task.resultUrl ?? null,
      },
    }),
    undefined
  )
}

function submitTurn(state: DmpChatState, turn: DmpChatTurn): DmpChatState {
  if (
    turn.threadId !== state.thread.id ||
    turn.chatHistoryId !== state.thread.chatHistoryId ||
    getActiveDmpChatTurn(state)
  ) {
    return state
  }

  const userMessage: DmpChatMessage = {
    id: turn.userMessageId,
    turnId: turn.id,
    role: 'user',
    text: turn.prompt,
    parts: [
      {
        type: 'text',
        id: turn.userMessageId,
        messageId: turn.userMessageId,
        text: turn.prompt,
        status: 'complete',
      },
    ],
    attachments: turn.attachments,
    status: 'complete',
    isOptimistic: true,
    createdAt: turn.createdAt,
  }
  const assistantMessage: DmpChatMessage = {
    id: turn.assistantMessageId,
    turnId: turn.id,
    role: 'assistant',
    text: '',
    parts: [],
    attachments: [],
    status: 'pending',
    isOptimistic: false,
    createdAt: turn.createdAt,
  }

  return {
    thread: {
      ...state.thread,
      messages: [...state.thread.messages, userMessage, assistantMessage],
      turns: [...state.thread.turns, turn],
      activeTurnId: turn.id,
      lastOutcome: null,
    },
  }
}

function requestCancellation(state: DmpChatState, turnId: string): DmpChatState {
  const turn = getActiveDmpChatTurn(state)
  if (!turn || turn.id !== turnId || turn.status === 'cancellation-requested') {
    return state
  }

  return replaceTurnAndMessages(
    state,
    turn.id,
    (currentTurn) => ({
      ...currentTurn,
      status: 'cancellation-requested',
    }),
    undefined
  )
}

function reconcileTask(
  state: DmpChatState,
  turnId: string,
  taskId: string,
  status: DmpChatTaskStatus,
  message?: string | null,
  rawUsage?: unknown
): DmpChatState {
  const turn = getActiveDmpChatTurn(state)
  if (!turn || turn.id !== turnId || turn.task.id !== taskId) {
    return state
  }

  const usage = normalizeDmpChatUsage(rawUsage)
  const stateWithUsage = usage ? setLastUsage(state, usage) : state

  switch (status) {
    case 'completed':
      return finishTurn(
        stateWithUsage,
        getActiveDmpChatTurn(stateWithUsage) ?? turn,
        {
          type: 'success',
          summary: DMP_CHAT_SUCCESS_SUMMARY,
        },
        undefined,
        message
      )
    case 'cancelled':
      return finishTurn(stateWithUsage, getActiveDmpChatTurn(stateWithUsage) ?? turn, {
        type: 'cancelled',
        summary: DMP_CHAT_CANCELLATION_SUMMARY,
      })
    case 'failed':
      return finishTurn(stateWithUsage, getActiveDmpChatTurn(stateWithUsage) ?? turn, {
        type: 'failure',
        summary: DMP_CHAT_FAILURE_SUMMARY,
      })
    case 'timed-out':
      return finishTurn(
        stateWithUsage,
        getActiveDmpChatTurn(stateWithUsage) ?? turn,
        {
          type: 'failure',
          summary: DMP_CHAT_TIMEOUT_SUMMARY,
        },
        'timed-out'
      )
    case 'creating':
    case 'accepted':
    case 'running':
      return replaceTurnAndMessages(
        state,
        turn.id,
        (currentTurn) => ({
          ...currentTurn,
          task: {
            ...currentTurn.task,
            status,
          },
        }),
        undefined
      )
  }
}

function applyAgUiEvent(state: DmpChatState, event: AGUIEvent): DmpChatState {
  if (isDmpChatUsageEvent(event)) {
    return applyDmpChatUsageEvent(state, event)
  }

  const turn = getActiveDmpChatTurn(state)
  if (!turn || !isSupportedDmpChatEvent(event) || !eventMatchesTurn(state, event, turn)) {
    return state
  }

  switch (event.type) {
    case EventType.RUN_STARTED:
      if (event.threadId !== turn.chatId || !isExpectedRunId(event.runId, turn)) {
        return state
      }

      return replaceTurnAndMessages(
        state,
        turn.id,
        (currentTurn) => ({
          ...currentTurn,
          status: currentTurn.status === 'cancellation-requested' ? 'cancellation-requested' : 'running',
          task: {
            ...currentTurn.task,
            status: 'running',
          },
        }),
        undefined
      )
    case EventType.TEXT_MESSAGE_START:
      if (event.role !== 'assistant' || !isAssistantTextStartEvent(event.messageId, turn)) {
        return state
      }

      return updateAssistantStreamMessage(state, turn, event.messageId, (message) => ({
        ...message,
        parts: addTextActivityPart(message.parts, event.messageId),
        status: 'streaming',
      }))
    case EventType.TEXT_MESSAGE_CONTENT:
      if (!isAssistantStreamEvent(event.messageId, turn)) {
        return state
      }

      return appendAssistantText(state, turn, event.messageId, event.delta)
    case EventType.TEXT_MESSAGE_CHUNK:
      if (!isAssistantStreamEvent(event.messageId, turn)) {
        return state
      }

      return event.delta ? appendAssistantText(state, turn, event.messageId, event.delta) : state
    case EventType.TEXT_MESSAGE_END:
      if (!isAssistantStreamEvent(event.messageId, turn)) {
        return state
      }

      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: completeTextActivityPart(message.parts, event.messageId),
      }))
    case EventType.REASONING_MESSAGE_START:
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: addReasoningActivityPart(message.parts, event.messageId),
      }))
    case EventType.REASONING_MESSAGE_CONTENT:
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: appendReasoningActivityPart(message.parts, event.messageId, event.delta),
      }))
    case EventType.REASONING_MESSAGE_CHUNK: {
      if (event.delta === undefined) {
        return state
      }

      const reasoningDelta = event.delta
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts:
          event.messageId === undefined
            ? appendLatestReasoningChunkActivityPart(message.parts, reasoningDelta)
            : appendReasoningChunkActivityPart(message.parts, event.messageId, reasoningDelta),
      }))
    }
    case EventType.REASONING_MESSAGE_END:
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: completeReasoningActivityPart(message.parts, event.messageId),
      }))
    case EventType.TOOL_CALL_START: {
      const parentMessageId = event.parentMessageId
      if (parentMessageId !== undefined && !isExpectedToolParent(parentMessageId, turn)) {
        return state
      }

      return updateAssistantActivityMessage(
        state,
        turn,
        (message) => ({
          ...message,
          parts: addToolCallActivityPart(message.parts, event.toolCallId, event.toolCallName),
        }),
        parentMessageId === undefined
          ? undefined
          : (currentTurn) => ({
              ...currentTurn,
              streamMessageId:
                parentMessageId === currentTurn.assistantMessageId ? currentTurn.streamMessageId : parentMessageId,
            })
      )
    }
    case EventType.TOOL_CALL_ARGS:
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: appendToolCallArguments(message.parts, event.toolCallId, event.delta),
      }))
    case EventType.TOOL_CALL_END:
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: completeToolCallArguments(message.parts, event.toolCallId),
      }))
    case EventType.TOOL_CALL_RESULT:
      return updateAssistantActivityMessage(state, turn, (message) => ({
        ...message,
        parts: attachToolCallResult(message.parts, event.toolCallId, event.content),
      }))
    case EventType.RUN_FINISHED: {
      if (event.threadId !== turn.chatId || !isExpectedRunId(event.runId, turn)) {
        return state
      }

      const outcomeStatus = getAgUiOutcomeStatus(event)
      const usage = getDmpChatUsageFromEvent(event)
      const stateWithUsage = usage ? setLastUsage(state, usage) : state
      if (event.outcome && outcomeStatus !== 'completed' && outcomeStatus !== 'cancelled') {
        return finishTurn(stateWithUsage, getActiveDmpChatTurn(stateWithUsage) ?? turn, {
          type: 'failure',
          summary: DMP_CHAT_FAILURE_SUMMARY,
        })
      }

      if (turn.status === 'cancellation-requested') {
        return outcomeStatus === 'cancelled'
          ? finishTurn(stateWithUsage, getActiveDmpChatTurn(stateWithUsage) ?? turn, {
              type: 'cancelled',
              summary: DMP_CHAT_CANCELLATION_SUMMARY,
            })
          : stateWithUsage
      }

      if (outcomeStatus === 'cancelled') {
        return finishTurn(stateWithUsage, getActiveDmpChatTurn(stateWithUsage) ?? turn, {
          type: 'cancelled',
          summary: DMP_CHAT_CANCELLATION_SUMMARY,
        })
      }

      return finishTurn(stateWithUsage, getActiveDmpChatTurn(stateWithUsage) ?? turn, {
        type: 'success',
        summary: DMP_CHAT_SUCCESS_SUMMARY,
      })
    }
    case EventType.RUN_ERROR:
      if (isCancellationEvent(event)) {
        return finishTurn(state, turn, {
          type: 'cancelled',
          summary: DMP_CHAT_CANCELLATION_SUMMARY,
        })
      }

      return finishTurn(state, turn, {
        type: 'failure',
        summary: isTimeoutEvent(event) ? DMP_CHAT_TIMEOUT_SUMMARY : DMP_CHAT_FAILURE_SUMMARY,
      })
    default:
      return state
  }
}

function isDmpChatUsageEvent(event: AGUIEvent): boolean {
  return event.type === EventType.CUSTOM && event.name === DMP_CHAT_USAGE_EVENT_NAME
}

function applyDmpChatUsageEvent(state: DmpChatState, event: AGUIEvent): DmpChatState {
  const usage = getDmpChatUsageFromEvent(event)
  if (!usage) {
    return state
  }

  const activeTurn = getActiveDmpChatTurn(state)
  if (activeTurn && usageEventMatchesTurn(event, activeTurn)) {
    return state
  }

  const latestCompletedTurn = [...state.thread.turns].reverse().find((turn) => !isDmpChatTurnActive(turn))
  if (!latestCompletedTurn || !usageEventMatchesTurn(event, latestCompletedTurn)) {
    return state
  }

  return setLastUsage(state, usage)
}

function getDmpChatUsageFromEvent(event: AGUIEvent): DmpChatUsage | null {
  if (event.type === EventType.CUSTOM) {
    return isDmpChatUsageEvent(event) ? normalizeDmpChatUsage(event.value) : null
  }

  if (event.type === EventType.RUN_FINISHED) {
    return getDmpChatUsage(event.result)
  }

  return null
}

function usageEventMatchesTurn(event: AGUIEvent, turn: DmpChatTurn): boolean {
  const correlatedEvent = event as AGUIEvent & {
    requestId?: unknown
    runId?: unknown
    threadId?: unknown
  }
  const hasCorrelation = typeof correlatedEvent.requestId === 'string' || typeof correlatedEvent.runId === 'string'

  return (
    hasCorrelation &&
    (correlatedEvent.requestId === undefined || correlatedEvent.requestId === turn.requestId) &&
    (correlatedEvent.runId === undefined ||
      (typeof correlatedEvent.runId === 'string' && isExpectedRunId(correlatedEvent.runId, turn))) &&
    (correlatedEvent.threadId === undefined || correlatedEvent.threadId === turn.chatId)
  )
}

function setLastUsage(state: DmpChatState, usage: DmpChatUsage): DmpChatState {
  return {
    thread: {
      ...state.thread,
      lastUsage: usage,
    },
  }
}

function eventMatchesTurn(state: DmpChatState, event: AGUIEvent, turn: DmpChatTurn): boolean {
  const correlatedEvent = event as AGUIEvent & {
    requestId?: unknown
    runId?: unknown
    threadId?: unknown
  }

  const requestMatches = correlatedEvent.requestId === turn.requestId
  const runMatches = typeof correlatedEvent.runId === 'string' && isExpectedRunId(correlatedEvent.runId, turn)
  const threadMatches = correlatedEvent.threadId === turn.chatId

  if (!hasEventCorrelation(event)) {
    if (event.type === EventType.RUN_ERROR) {
      return true
    }

    if (event.type === EventType.TOOL_CALL_START) {
      const parentMessageId = (event as AGUIEvent & { parentMessageId?: unknown }).parentMessageId
      return (
        turn.status === 'running' && typeof parentMessageId === 'string' && isKnownToolParent(parentMessageId, turn)
      )
    }

    return turn.status === 'running' && (isUncorrelatedTextEvent(event) || hasMatchingActivityPart(state, event, turn))
  }

  if (
    (typeof correlatedEvent.requestId === 'string' && !requestMatches) ||
    (typeof correlatedEvent.runId === 'string' && !runMatches) ||
    (typeof correlatedEvent.threadId === 'string' && !threadMatches)
  ) {
    return false
  }

  return true
}

function isUncorrelatedTextEvent(event: AGUIEvent): boolean {
  return (
    event.type === EventType.TEXT_MESSAGE_START ||
    event.type === EventType.TEXT_MESSAGE_CONTENT ||
    event.type === EventType.TEXT_MESSAGE_CHUNK ||
    event.type === EventType.TEXT_MESSAGE_END
  )
}

function hasMatchingActivityPart(state: DmpChatState, event: AGUIEvent, turn: DmpChatTurn): boolean {
  const assistantMessage = state.thread.messages.find((message) => message.id === turn.assistantMessageId)
  if (!assistantMessage) {
    return false
  }

  if (
    event.type === EventType.REASONING_MESSAGE_CONTENT ||
    event.type === EventType.REASONING_MESSAGE_END ||
    event.type === EventType.REASONING_MESSAGE_CHUNK
  ) {
    return assistantMessage.parts.some(
      (part) =>
        part.type === 'reasoning' &&
        part.status === 'streaming' &&
        (event.messageId === undefined || part.messageId === event.messageId)
    )
  }

  if (
    event.type === EventType.TOOL_CALL_ARGS ||
    event.type === EventType.TOOL_CALL_END ||
    event.type === EventType.TOOL_CALL_RESULT
  ) {
    return assistantMessage.parts.some(
      (part) =>
        part.type === 'tool-call' &&
        part.toolCallId === event.toolCallId &&
        (event.type === EventType.TOOL_CALL_ARGS ? part.status === 'streaming' : part.result === null)
    )
  }

  return false
}

function isExpectedRunId(runId: string, turn: DmpChatTurn): boolean {
  return runId === turn.runId || runId === turn.requestId
}

function hasEventCorrelation(event: AGUIEvent): boolean {
  const correlatedEvent = event as AGUIEvent & {
    requestId?: unknown
    runId?: unknown
    threadId?: unknown
  }

  return (
    typeof correlatedEvent.requestId === 'string' ||
    typeof correlatedEvent.runId === 'string' ||
    typeof correlatedEvent.threadId === 'string'
  )
}

function getAgUiOutcomeStatus(event: AGUIEvent): DmpChatTaskStatus | null {
  const outcome = (event as AGUIEvent & { outcome?: unknown }).outcome
  if (typeof outcome === 'string') {
    return normalizeDmpChatTaskStatus(outcome)
  }

  if (!outcome || typeof outcome !== 'object') {
    return null
  }

  const outcomeRecord = outcome as Record<string, unknown>
  return normalizeDmpChatTaskStatus(outcomeRecord.type ?? outcomeRecord.status)
}

function isCancellationEvent(event: AGUIEvent): boolean {
  if (event.type !== EventType.RUN_ERROR) {
    return false
  }

  const errorEvent = event as AGUIEvent & {
    code?: unknown
    message?: unknown
  }
  return normalizeDmpChatTaskStatus(errorEvent.code) === 'cancelled' || hasKeyword(errorEvent.message, 'cancel')
}

function isTimeoutEvent(event: AGUIEvent): boolean {
  if (event.type !== EventType.RUN_ERROR) {
    return false
  }

  const errorEvent = event as AGUIEvent & {
    code?: unknown
    message?: unknown
  }
  return (
    normalizeDmpChatTaskStatus(errorEvent.code) === 'timed-out' ||
    hasKeyword(errorEvent.message, 'timeout') ||
    hasKeyword(errorEvent.message, 'timed out')
  )
}

function hasKeyword(value: unknown, keyword: string): boolean {
  return typeof value === 'string' && value.toLowerCase().includes(keyword)
}

function replaceTurnAndMessages(
  state: DmpChatState,
  turnId: string,
  updateTurn: (turn: DmpChatTurn) => DmpChatTurn,
  updateMessage: ((message: DmpChatMessage) => DmpChatMessage) | undefined
): DmpChatState {
  return {
    thread: {
      ...state.thread,
      turns: state.thread.turns.map((turn) => (turn.id === turnId ? updateTurn(turn) : turn)),
      messages: updateMessage
        ? state.thread.messages.map((message) => (message.turnId === turnId ? updateMessage(message) : message))
        : state.thread.messages,
    },
  }
}

function updateAssistantStreamMessage(
  state: DmpChatState,
  turn: DmpChatTurn,
  streamMessageId: string | undefined,
  updateMessage: (message: DmpChatMessage) => DmpChatMessage
): DmpChatState {
  return replaceTurnAndMessages(
    state,
    turn.id,
    (currentTurn) => ({
      ...currentTurn,
      streamMessageId:
        streamMessageId && streamMessageId !== currentTurn.assistantMessageId
          ? streamMessageId
          : currentTurn.streamMessageId,
    }),
    (message) => (message.id === turn.assistantMessageId ? updateMessage(message) : message)
  )
}

function appendAssistantText(
  state: DmpChatState,
  turn: DmpChatTurn,
  streamMessageId: string | undefined,
  delta: string
): DmpChatState {
  const currentMessage = state.thread.messages.find((message) => message.id === turn.assistantMessageId)
  const remainingCharacters = DMP_CHAT_MAX_RESPONSE_CHARS - (currentMessage?.text.length ?? 0)
  if (remainingCharacters <= 0) {
    return state
  }

  const isTruncated = delta.length > remainingCharacters
  const truncationNotice =
    isTruncated && remainingCharacters >= DMP_CHAT_RESPONSE_TRUNCATED_NOTICE.length
      ? DMP_CHAT_RESPONSE_TRUNCATED_NOTICE
      : ''
  const visibleDeltaLength = remainingCharacters - truncationNotice.length
  const visibleDelta = delta.slice(0, visibleDeltaLength) + truncationNotice
  const activityMessageId = streamMessageId ?? turn.streamMessageId ?? turn.assistantMessageId

  return updateAssistantStreamMessage(state, turn, streamMessageId, (message) => ({
    ...message,
    text: message.text + visibleDelta,
    parts: appendTextActivityPart(message.parts, activityMessageId, visibleDelta),
    status: 'streaming',
  }))
}

function updateAssistantActivityMessage(
  state: DmpChatState,
  turn: DmpChatTurn,
  updateMessage: (message: DmpChatMessage) => DmpChatMessage,
  updateTurn?: (turn: DmpChatTurn) => DmpChatTurn
): DmpChatState {
  const currentMessage = state.thread.messages.find((message) => message.id === turn.assistantMessageId)
  if (!currentMessage) {
    return state
  }

  const nextMessage = updateMessage(currentMessage)
  if (nextMessage.parts === currentMessage.parts) {
    return state
  }

  return replaceTurnAndMessages(state, turn.id, updateTurn ?? ((currentTurn) => currentTurn), (message) =>
    message.id === turn.assistantMessageId ? nextMessage : message
  )
}

function addTextActivityPart(parts: readonly DmpChatActivityPart[], messageId: string): readonly DmpChatActivityPart[] {
  return [
    ...parts,
    {
      type: 'text',
      id: createActivityPartId(messageId, parts.length),
      messageId,
      text: '',
      status: 'streaming',
    },
  ]
}

function appendTextActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string,
  delta: string
): readonly DmpChatActivityPart[] {
  if (!delta) {
    return parts
  }

  const lastPart = parts[parts.length - 1]
  if (lastPart?.type === 'text' && lastPart.messageId === messageId && lastPart.status === 'streaming') {
    const availableCharacters = DMP_CHAT_MAX_ACTIVITY_CHARS - lastPart.text.length
    if (availableCharacters <= 0) {
      return parts
    }

    const visibleDelta = delta.slice(0, availableCharacters)
    if (!visibleDelta) {
      return parts
    }

    return [
      ...parts.slice(0, -1),
      {
        ...lastPart,
        text: lastPart.text + visibleDelta,
      },
    ]
  }

  return [
    ...parts,
    {
      type: 'text',
      id: createActivityPartId(messageId, parts.length),
      messageId,
      text: delta.slice(0, DMP_CHAT_MAX_ACTIVITY_CHARS),
      status: 'streaming',
    },
  ]
}

function completeTextActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string
): readonly DmpChatActivityPart[] {
  let changed = false
  const nextParts = parts.map((part) => {
    if (part.type !== 'text' || part.messageId !== messageId || part.status !== 'streaming') {
      return part
    }

    changed = true
    return {
      ...part,
      status: 'complete' as const,
    }
  })

  return changed ? nextParts : parts
}

function addReasoningActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string
): readonly DmpChatActivityPart[] {
  return [
    ...parts,
    {
      type: 'reasoning',
      id: createActivityPartId(`reasoning-${messageId}`, parts.length),
      messageId,
      text: '',
      status: 'streaming',
    },
  ]
}

function appendReasoningActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string,
  delta: string
): readonly DmpChatActivityPart[] {
  const part = findLatestReasoningActivityPart(parts, messageId)
  if (!part) {
    return parts
  }

  return appendReasoningTextToPart(parts, part, delta)
}

function findLatestReasoningActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string
): DmpChatReasoningPart | undefined {
  return [...parts]
    .reverse()
    .find(
      (candidate): candidate is DmpChatReasoningPart =>
        candidate.type === 'reasoning' && candidate.messageId === messageId && candidate.status === 'streaming'
    )
}

function appendReasoningTextToPart(
  parts: readonly DmpChatActivityPart[],
  part: DmpChatReasoningPart,
  delta: string
): readonly DmpChatActivityPart[] {
  const availableCharacters = DMP_CHAT_MAX_ACTIVITY_CHARS - part.text.length
  if (availableCharacters <= 0) {
    return parts
  }

  const visibleDelta = delta.slice(0, availableCharacters)
  if (!visibleDelta) {
    return parts
  }

  return parts.map((candidate) =>
    candidate === part
      ? {
          ...part,
          text: part.text + visibleDelta,
        }
      : candidate
  )
}

function appendReasoningChunkActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string,
  delta: string
): readonly DmpChatActivityPart[] {
  const part = findLatestReasoningActivityPart(parts, messageId)
  if (part) {
    return appendReasoningTextToPart(parts, part, delta)
  }

  const nextParts = addReasoningActivityPart(parts, messageId)
  const newPart = nextParts[nextParts.length - 1]
  if (newPart?.type !== 'reasoning') {
    return parts
  }

  return appendReasoningTextToPart(nextParts, newPart, delta)
}

function appendLatestReasoningChunkActivityPart(
  parts: readonly DmpChatActivityPart[],
  delta: string
): readonly DmpChatActivityPart[] {
  const part = [...parts]
    .reverse()
    .find(
      (candidate): candidate is DmpChatReasoningPart =>
        candidate.type === 'reasoning' && candidate.status === 'streaming'
    )
  if (!part) {
    return parts
  }

  return appendReasoningTextToPart(parts, part, delta)
}

function completeReasoningActivityPart(
  parts: readonly DmpChatActivityPart[],
  messageId: string
): readonly DmpChatActivityPart[] {
  const part = [...parts]
    .reverse()
    .find(
      (candidate): candidate is DmpChatReasoningPart =>
        candidate.type === 'reasoning' && candidate.messageId === messageId && candidate.status === 'streaming'
    )
  if (!part) {
    return parts
  }

  return parts.map((candidate) =>
    candidate === part
      ? {
          ...part,
          status: 'complete' as const,
        }
      : candidate
  )
}

function addToolCallActivityPart(
  parts: readonly DmpChatActivityPart[],
  toolCallId: string,
  toolName: string
): readonly DmpChatActivityPart[] {
  return [
    ...parts,
    {
      type: 'tool-call',
      id: createActivityPartId(`tool-${toolCallId}`, parts.length),
      toolCallId,
      toolName,
      argsText: '',
      result: null,
      status: 'streaming',
    },
  ]
}

function appendToolCallArguments(
  parts: readonly DmpChatActivityPart[],
  toolCallId: string,
  delta: string
): readonly DmpChatActivityPart[] {
  const part = [...parts]
    .reverse()
    .find(
      (candidate): candidate is DmpChatToolCallPart =>
        candidate.type === 'tool-call' && candidate.toolCallId === toolCallId && candidate.status === 'streaming'
    )
  if (!part) {
    return parts
  }

  const availableCharacters = DMP_CHAT_MAX_ACTIVITY_CHARS - part.argsText.length
  if (availableCharacters <= 0) {
    return parts
  }

  const visibleDelta = delta.slice(0, availableCharacters)
  if (!visibleDelta) {
    return parts
  }

  return parts.map((candidate) =>
    candidate === part
      ? {
          ...part,
          argsText: part.argsText + visibleDelta,
        }
      : candidate
  )
}

function completeToolCallArguments(
  parts: readonly DmpChatActivityPart[],
  toolCallId: string
): readonly DmpChatActivityPart[] {
  const part = [...parts]
    .reverse()
    .find(
      (candidate): candidate is DmpChatToolCallPart =>
        candidate.type === 'tool-call' && candidate.toolCallId === toolCallId && candidate.status === 'streaming'
    )
  if (!part) {
    return parts
  }

  return parts.map((candidate) =>
    candidate === part
      ? {
          ...part,
          status: 'incomplete' as const,
        }
      : candidate
  )
}

function attachToolCallResult(
  parts: readonly DmpChatActivityPart[],
  toolCallId: string,
  result: string
): readonly DmpChatActivityPart[] {
  const part = [...parts]
    .reverse()
    .find(
      (candidate): candidate is DmpChatToolCallPart =>
        candidate.type === 'tool-call' && candidate.toolCallId === toolCallId && candidate.result === null
    )
  if (!part) {
    return parts
  }

  return parts.map((candidate) =>
    candidate === part
      ? {
          ...part,
          result: result.slice(0, DMP_CHAT_MAX_ACTIVITY_CHARS),
          status: 'complete' as const,
        }
      : candidate
  )
}

function finalizeActivityParts(
  parts: readonly DmpChatActivityPart[],
  outcomeType: DmpChatTerminalOutcome['type']
): readonly DmpChatActivityPart[] {
  return parts.map((part) => {
    if (part.type === 'tool-call') {
      return part.result === null && part.status !== 'complete'
        ? {
            ...part,
            status: 'incomplete' as const,
          }
        : part
    }

    if (part.status !== 'streaming') {
      return part
    }

    return {
      ...part,
      status: part.type === 'reasoning' && outcomeType !== 'success' ? 'incomplete' : 'complete',
    }
  })
}

function createActivityPartId(messageId: string, partIndex: number): string {
  return `activity-${messageId}-${partIndex}`
}

function isExpectedToolParent(parentMessageId: string | undefined, turn: DmpChatTurn): boolean {
  return (
    parentMessageId !== undefined &&
    (parentMessageId === turn.assistantMessageId ||
      turn.streamMessageId === null ||
      parentMessageId === turn.streamMessageId ||
      turn.status === 'running')
  )
}

function isKnownToolParent(parentMessageId: string, turn: DmpChatTurn): boolean {
  return parentMessageId === turn.assistantMessageId || parentMessageId === turn.streamMessageId
}

function isSafeActivityIdentity(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= DMP_CHAT_MAX_ACTIVITY_CHARS &&
    !hasUnsafeActivityIdentityCharacter(value)
  )
}

function isSafeActivityText(value: unknown): value is string {
  return typeof value === 'string' && value.length <= DMP_CHAT_MAX_ACTIVITY_CHARS
}

function hasUnsafeActivityIdentityCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0
    if (codePoint <= 0x1f || codePoint === 0x7f) {
      return true
    }
  }

  return false
}

function isAssistantStreamEvent(messageId: string | undefined, turn: DmpChatTurn): boolean {
  return (
    !messageId ||
    messageId === turn.assistantMessageId ||
    turn.streamMessageId === null ||
    messageId === turn.streamMessageId
  )
}

function isAssistantTextStartEvent(messageId: string | undefined, turn: DmpChatTurn): boolean {
  return isAssistantStreamEvent(messageId, turn) || (turn.status === 'running' && messageId !== undefined)
}

function isSupportedDmpChatEvent(event: AGUIEvent): boolean {
  return (
    event.type === EventType.RUN_STARTED ||
    event.type === EventType.TEXT_MESSAGE_START ||
    event.type === EventType.TEXT_MESSAGE_CONTENT ||
    event.type === EventType.TEXT_MESSAGE_CHUNK ||
    event.type === EventType.TEXT_MESSAGE_END ||
    event.type === EventType.REASONING_MESSAGE_START ||
    event.type === EventType.REASONING_MESSAGE_CONTENT ||
    event.type === EventType.REASONING_MESSAGE_CHUNK ||
    event.type === EventType.REASONING_MESSAGE_END ||
    event.type === EventType.TOOL_CALL_START ||
    event.type === EventType.TOOL_CALL_ARGS ||
    event.type === EventType.TOOL_CALL_END ||
    event.type === EventType.TOOL_CALL_RESULT ||
    event.type === EventType.CUSTOM ||
    event.type === EventType.RUN_FINISHED ||
    event.type === EventType.RUN_ERROR
  )
}

function sanitizeDmpChatEvent(event: AGUIEvent): AGUIEvent | null {
  if (!isSupportedDmpChatEvent(event)) {
    return null
  }

  const source = event as AGUIEvent & Record<string, unknown>
  const sanitized: Record<string, unknown> = {
    type: event.type,
  }

  if (typeof source.timestamp === 'number') {
    sanitized.timestamp = source.timestamp
  }
  for (const key of ['threadId', 'runId', 'requestId']) {
    const value = source[key]
    if (value !== undefined) {
      if (typeof value !== 'string') {
        return null
      }
      sanitized[key] = value
    }
  }

  switch (event.type) {
    case EventType.TEXT_MESSAGE_START:
      if (typeof event.messageId !== 'string' || event.role !== 'assistant') {
        return null
      }
      sanitized.messageId = event.messageId
      sanitized.role = event.role
      break
    case EventType.TEXT_MESSAGE_CONTENT:
      if (typeof event.messageId !== 'string' || typeof event.delta !== 'string') {
        return null
      }
      sanitized.messageId = event.messageId
      sanitized.delta = event.delta
      break
    case EventType.TEXT_MESSAGE_CHUNK:
      if (event.messageId !== undefined && typeof event.messageId !== 'string') {
        return null
      }
      if (event.delta !== undefined && typeof event.delta !== 'string') {
        return null
      }
      if (event.messageId !== undefined) {
        sanitized.messageId = event.messageId
      }
      if (event.delta !== undefined) {
        sanitized.delta = event.delta
      }
      break
    case EventType.TEXT_MESSAGE_END:
      if (typeof event.messageId !== 'string') {
        return null
      }
      sanitized.messageId = event.messageId
      break
    case EventType.REASONING_MESSAGE_START:
      if (!isSafeActivityIdentity(event.messageId) || event.role !== 'reasoning') {
        return null
      }
      sanitized.messageId = event.messageId
      sanitized.role = event.role
      break
    case EventType.REASONING_MESSAGE_CONTENT:
      if (!isSafeActivityIdentity(event.messageId) || !isSafeActivityText(event.delta)) {
        return null
      }
      sanitized.messageId = event.messageId
      sanitized.delta = event.delta
      break
    case EventType.REASONING_MESSAGE_CHUNK:
      if (event.messageId !== undefined && !isSafeActivityIdentity(event.messageId)) {
        return null
      }
      if (event.delta !== undefined && !isSafeActivityText(event.delta)) {
        return null
      }
      if (event.messageId !== undefined) {
        sanitized.messageId = event.messageId
      }
      if (event.delta !== undefined) {
        sanitized.delta = event.delta
      }
      break
    case EventType.REASONING_MESSAGE_END:
      if (!isSafeActivityIdentity(event.messageId)) {
        return null
      }
      sanitized.messageId = event.messageId
      break
    case EventType.TOOL_CALL_START:
      if (!isSafeActivityIdentity(event.toolCallId) || !isSafeActivityIdentity(event.toolCallName)) {
        return null
      }
      sanitized.toolCallId = event.toolCallId
      sanitized.toolCallName = event.toolCallName
      if (event.parentMessageId !== undefined) {
        if (!isSafeActivityIdentity(event.parentMessageId)) {
          return null
        }
        sanitized.parentMessageId = event.parentMessageId
      }
      break
    case EventType.TOOL_CALL_ARGS:
      if (!isSafeActivityIdentity(event.toolCallId) || !isSafeActivityText(event.delta)) {
        return null
      }
      sanitized.toolCallId = event.toolCallId
      sanitized.delta = event.delta
      break
    case EventType.TOOL_CALL_END:
      if (!isSafeActivityIdentity(event.toolCallId)) {
        return null
      }
      sanitized.toolCallId = event.toolCallId
      break
    case EventType.TOOL_CALL_RESULT:
      if (
        !isSafeActivityIdentity(event.messageId) ||
        !isSafeActivityIdentity(event.toolCallId) ||
        !isSafeActivityText(event.content) ||
        (event.role !== undefined && event.role !== 'tool')
      ) {
        return null
      }
      sanitized.messageId = event.messageId
      sanitized.toolCallId = event.toolCallId
      sanitized.content = event.content
      if (event.role !== undefined) {
        sanitized.role = event.role
      }
      break
    case EventType.CUSTOM: {
      if (event.name !== DMP_CHAT_USAGE_EVENT_NAME) {
        return null
      }

      const usage = normalizeDmpChatUsage(event.value)
      if (!usage) {
        return null
      }

      sanitized.name = DMP_CHAT_USAGE_EVENT_NAME
      sanitized.value = usage
      break
    }
    case EventType.RUN_FINISHED: {
      const outcome = getSafeOutcome(event)
      if (outcome) {
        sanitized.outcome = outcome
      }
      const usage = getDmpChatUsage(event.result)
      if (usage) {
        sanitized.result = { usage }
      }
      break
    }
    case EventType.RUN_ERROR:
      sanitized.message = classifyRunError(event.message)
      if (typeof event.code === 'string') {
        const normalizedCode = normalizeDmpChatTaskStatus(event.code)
        if (normalizedCode) {
          sanitized.code = normalizedCode
        }
      }
      break
    case EventType.RUN_STARTED:
      break
  }

  return sanitized as AGUIEvent
}

function getSafeOutcome(event: AGUIEvent): { type: string } | null {
  const outcome = (event as AGUIEvent & { outcome?: unknown }).outcome
  if (typeof outcome === 'string') {
    return { type: normalizeDmpChatTaskStatus(outcome) ?? 'unsupported' }
  }

  if (!outcome || typeof outcome !== 'object') {
    return null
  }

  const outcomeRecord = outcome as Record<string, unknown>
  const type = outcomeRecord.type ?? outcomeRecord.status
  return typeof type === 'string' ? { type: normalizeDmpChatTaskStatus(type) ?? 'unsupported' } : null
}

function classifyRunError(message: string): string {
  const normalizedMessage = message.toLowerCase()
  if (normalizedMessage.includes('cancel')) {
    return 'cancelled'
  }
  if (normalizedMessage.includes('timeout') || normalizedMessage.includes('timed out')) {
    return 'timed out'
  }
  return 'worker failure'
}

function finishTurn(
  state: DmpChatState,
  turn: DmpChatTurn,
  outcome: DmpChatTerminalOutcome,
  taskStatusOverride?: DmpChatTaskStatus,
  assistantText?: string | null
): DmpChatState {
  const safeOutcome = normalizeDmpChatTerminalOutcome(state, turn, outcome)
  const { turnStatus, taskStatus, messageStatus } = TERMINAL_STATUS_BY_OUTCOME[safeOutcome.type]

  const nextState = replaceTurnAndMessages(
    state,
    turn.id,
    (currentTurn) => ({
      ...currentTurn,
      status: turnStatus,
      task: {
        ...currentTurn.task,
        status: taskStatusOverride ?? taskStatus,
      },
      outcome: safeOutcome,
    }),
    (message) =>
      message.id === turn.assistantMessageId
        ? {
            ...message,
            text: assistantText?.trim() || message.text || safeOutcome.summary,
            parts: finalizeActivityParts(message.parts, safeOutcome.type),
            status: messageStatus,
          }
        : message.id === turn.userMessageId
          ? {
              ...message,
              isOptimistic: false,
            }
          : message
  )

  return {
    thread: {
      ...nextState.thread,
      activeTurnId: null,
      lastOutcome: safeOutcome,
    },
  }
}

function normalizeDmpChatTerminalOutcome(
  state: DmpChatState,
  turn: DmpChatTurn,
  outcome: DmpChatTerminalOutcome
): DmpChatTerminalOutcome {
  if (outcome.type !== 'success') {
    return outcome
  }

  const assistantMessage = state.thread.messages.find((message) => message.id === turn.assistantMessageId)
  return assistantMessage && hasIncompleteToolCall(assistantMessage.parts)
    ? {
        type: 'failure',
        summary: DMP_CHAT_FAILURE_SUMMARY,
      }
    : outcome
}

function toAssistantMessageStatus(message: DmpChatMessage): ThreadAssistantMessage['status'] {
  switch (message.status) {
    case 'complete':
      return hasIncompleteToolCall(message.parts)
        ? { type: 'incomplete', reason: 'tool-calls' }
        : { type: 'complete', reason: 'stop' }
    case 'error':
      return { type: 'incomplete', reason: 'error' }
    case 'cancelled':
      return { type: 'incomplete', reason: 'cancelled' }
    case 'pending':
    case 'streaming':
      return { type: 'running' }
  }
}

function hasIncompleteToolCall(parts: readonly DmpChatActivityPart[]): boolean {
  return parts.some((part) => part.type === 'tool-call' && part.status !== 'complete')
}

function toAssistantActivityPartStatus(
  status: DmpChatActivityPartStatus
): { type: 'running' } | { type: 'complete' } | { type: 'incomplete'; reason: 'other' } {
  switch (status) {
    case 'streaming':
      return { type: 'running' }
    case 'complete':
      return { type: 'complete' }
    case 'incomplete':
      return { type: 'incomplete', reason: 'other' }
  }
}

type DmpChatProjectedToolArguments = Extract<ThreadAssistantMessagePart, { type: 'tool-call' }>['args']

function parseDmpChatToolArguments(argsText: string): DmpChatProjectedToolArguments {
  if (!argsText) {
    return {}
  }

  try {
    const parsed: unknown = JSON.parse(argsText)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as DmpChatProjectedToolArguments)
      : ({} as DmpChatProjectedToolArguments)
  } catch {
    return {}
  }
}

function projectDmpChatActivityParts(
  message: DmpChatMessage,
  toolDisplayMode: DmpChatToolDisplayMode
): readonly ThreadAssistantMessagePart[] {
  const projectedParts = message.parts.flatMap<ThreadAssistantMessagePart>((part) => {
    if (part.type === 'text' || part.type === 'reasoning') {
      if (!part.text || (part.type === 'reasoning' && !part.text.trim())) {
        return []
      }

      return [
        {
          type: part.type,
          text: part.text,
          status: toAssistantActivityPartStatus(part.status),
        },
      ]
    }

    const toolPart: ThreadAssistantMessagePart = {
      type: 'tool-call',
      toolCallId: part.toolCallId,
      toolName: part.toolName,
      args: toolDisplayMode === 'name-only' ? {} : parseDmpChatToolArguments(part.argsText),
      argsText: toolDisplayMode === 'name-only' ? '' : part.argsText,
    }
    return toolDisplayMode === 'full-details' && part.result !== null
      ? [{ ...toolPart, result: part.result }]
      : [toolPart]
  })

  const hasProjectedText = projectedParts.some((part) => part.type === 'text')
  const fallbackTextPart: ThreadAssistantMessagePart = {
    type: 'text',
    text: message.text,
    status: toAssistantActivityPartStatus(
      message.status === 'pending' || message.status === 'streaming' ? 'streaming' : 'complete'
    ),
  }

  if (hasProjectedText || !message.text) {
    return projectedParts.length > 0 ? projectedParts : [fallbackTextPart]
  }

  return [...projectedParts, fallbackTextPart]
}

export function projectDmpChatAttachment(attachment: DmpChatAttachment): CompleteAttachment {
  return {
    id: attachment.inputRef,
    type: getDmpChatAttachmentType(attachment.contentType, attachment.filename),
    name: attachment.filename,
    ...(attachment.contentType ? { contentType: attachment.contentType } : {}),
    ...(attachment.kind ? { kind: attachment.kind } : {}),
    ...(attachment.sizeBytes != null ? { sizeBytes: attachment.sizeBytes } : {}),
    ...(attachment.file ? { file: attachment.file } : {}),
    status: { type: 'complete' },
    content: [],
  }
}

export function projectDmpChatMessages(
  thread: DmpChatThread,
  toolDisplayMode: DmpChatToolDisplayMode = 'full-details'
): readonly ThreadMessage[] {
  return thread.messages.map((message) => {
    if (message.role === 'user') {
      return {
        id: message.id,
        role: 'user',
        content: [{ type: 'text' as const, text: message.text }],
        attachments: message.attachments.map(projectDmpChatAttachment),
        metadata: {
          custom: {},
          isOptimistic: message.isOptimistic,
        },
        createdAt: message.createdAt,
      }
    }

    return {
      id: message.id,
      role: 'assistant',
      content: projectDmpChatActivityParts(message, toolDisplayMode),
      status: toAssistantMessageStatus(message),
      metadata: {
        unstable_state: {},
        unstable_annotations: [],
        unstable_data: [],
        steps: [],
        custom: {},
      },
      createdAt: message.createdAt,
    }
  })
}
